import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import logo from '../assets/logo.jpg';

const Header = ({ toggleSidebar }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  const { currentUser, signOut, isAdmin } = useAuth();
  const { wallets } = useWallet();
  const walletA = wallets?.walletA || 0;
  const walletB = wallets?.walletB || 0;
  const location = useLocation();
  
  // Close dropdown when route changes
  useEffect(() => {
    setShowDropdown(false);
  }, [location]);
  
  // Handle scrolling effects
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const toggleDarkMode = () => {
    if (document.documentElement.classList.contains('dark')) {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    } else {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleLogout = () => {
    signOut();
  };

  // Get display name for header (email, phone, or first initial)
  const getDisplayIdentifier = () => {
    if (!currentUser) return 'Demo User';
    if (currentUser.email) return currentUser.email;
    if (currentUser.phoneNumber) return currentUser.phoneNumber;
    return currentUser.displayName ? currentUser.displayName[0] : 'U';
  };

  // Get first letter for avatar
  const getAvatarLetter = () => {
    if (!currentUser) return 'D';
    if (currentUser.displayName) return currentUser.displayName[0];
    if (currentUser.email) return currentUser.email[0];
    return 'U';
  };

  return (
    <header className={`fixed w-full top-0 z-40 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg' 
        : 'bg-white dark:bg-gray-900'
    }`}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleSidebar}
              className="lg:hidden text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
              aria-label="Toggle navigation"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <Link to="/" className="flex items-center gap-3 group transition-all duration-300">
              <div className="h-10 w-10 rounded-full overflow-hidden shadow-lg ring-2 ring-blue-500 dark:ring-blue-400">
                <img src={logo} alt="GamePreBattle Logo" className="h-full w-full object-cover transform group-hover:scale-110 transition-transform duration-300" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 text-transparent bg-clip-text group-hover:from-indigo-600 group-hover:to-blue-600 dark:group-hover:from-indigo-400 dark:group-hover:to-blue-400 transition-all duration-300">
                GamePreBattle
              </span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <NavLink 
              to="/"
              className={({isActive}) => 
                `px-4 py-2 rounded-full text-base font-medium transition-all duration-200
                ${isActive 
                  ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`
              }
              end
            >
              Home
            </NavLink>
            
            <NavLink 
              to="/matches"
              className={({isActive}) => 
                `px-4 py-2 rounded-full text-base font-medium transition-all duration-200
                ${isActive 
                  ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`
              }
            >
              Matches
            </NavLink>
            
            <NavLink 
              to="/wallet"
              className={({isActive}) => 
                `px-4 py-2 rounded-full text-base font-medium transition-all duration-200
                ${isActive 
                  ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`
              }
            >
              Wallet
            </NavLink>
            
            <NavLink 
              to="/earn"
              className={({isActive}) => 
                `px-4 py-2 rounded-full text-base font-medium transition-all duration-200
                ${isActive 
                  ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`
              }
            >
              Earn Coins
            </NavLink>
            
            <NavLink 
              to="/prediction-rules"
              className={({isActive}) => 
                `px-4 py-2 rounded-full text-base font-medium transition-all duration-200
                ${isActive 
                  ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`
              }
            >
              Rules
            </NavLink>
            
            {!currentUser && (
              <>
                <NavLink 
                  to="/login"
                  className={({isActive}) => 
                    `px-4 py-2 rounded-full text-base font-medium transition-all duration-200 ml-2
                    ${isActive 
                      ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`
                  }
                >
                  Login
                </NavLink>
                
                <NavLink 
                  to="/register"
                  className={({isActive}) => 
                    `px-4 py-2 rounded-full text-base font-medium transition-all duration-200 ml-2
                    ${isActive 
                      ? 'bg-gradient-to-r from-blue-700 to-indigo-700 text-white shadow-md' 
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:from-blue-700 hover:to-indigo-700'
                    }`
                  }
                >
                  Register
                </NavLink>
              </>
            )}
          </nav>
          
          <div className="hidden md:flex items-center gap-3">
            <div className="flex flex-col items-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 px-4 py-2 rounded-full shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">Wallet A</span>
                <span className="font-medium text-gray-800 dark:text-white">{walletA.toLocaleString()} coins</span>
              </div>
            </div>
            <div className="flex flex-col items-center bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 px-4 py-2 rounded-full shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-green-600 dark:text-green-400">Wallet B</span>
                <span className="font-medium text-gray-800 dark:text-white">₹{(walletB / 100).toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleDarkMode}
              className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle dark mode"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            </button>
            
            {/* Profile dropdown */}
            {currentUser ? (
              <div className="relative">
                <button
                  onClick={toggleDropdown}
                  className="flex items-center gap-2 focus:outline-none"
                  aria-expanded={showDropdown}
                  aria-haspopup="true"
                >
                  <div className="h-10 w-10 rounded-full bg-blue-600 dark:bg-blue-500 text-white flex items-center justify-center font-semibold text-lg">
                    {getAvatarLetter().toUpperCase()}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {currentUser?.displayName || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {walletA.toLocaleString()} coins
                    </p>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-50">
                    <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{currentUser?.displayName || 'User'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser?.email || ''}</p>
                    </div>
                    
                    <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        Profile
                      </Link>
                    
                    <Link to="/wallet" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        Wallet
                      </Link>
                    
                    <Link to="/referrals" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        Referrals
                    </Link>
                    
                    {isAdmin && (
                      <Link to="/admin/dashboard" className="block px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        Admin Panel
                      </Link>
                    )}
                    
                      <button
                        onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                      Sign out
                      </button>
                  </div>
                )}
              </div>
            ) : (
              null
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 